import { useState } from 'react';
import { Link, useLocation } from 'react-router';

import { AppRoutesEnum } from '@/lib/constants';
import { useResponsive } from '@/hooks/useResponsive';

export function BottomNavigation() {
  const location = useLocation();
  const { isMobile } = useResponsive();

  const NavButton = ({ to, icon, text }: { to: string; icon: 'junk' | 'collect' | 'lottery' | 'refer'; text: string }) => {
    const isActive = location.pathname === to;
    const [isHovered, setIsHovered] = useState(false);
    
    // Detect if this is the rewards button (desktop special styling)
    const isRewards = icon === 'lottery' && to === AppRoutesEnum.Rewards;
    
    // Calculate icon color based on state
    const iconColor = isMobile 
      ? (isActive ? '#50E4F9' : '#FFFFFF')
      : isRewards
        ? '#FFFFFF' // Rewards icon always stays white on desktop
        : isActive ? '#50E4F9' : '#FFFFFF'; // Non-rewards: blue when active, white otherwise
    
    // Determine which buttons should show right borders (separators)
    // On mobile: all buttons except the last one (Refer)
    // On desktop: different logic for special buttons
    const shouldShowRightBorder = isMobile 
      ? icon !== 'refer' // All mobile buttons get right border except last
      : to !== AppRoutesEnum.Referral && !(icon === 'collect' || icon === 'lottery');

    const getIcon = () => {
      const iconSize = window.innerWidth < 768 ? 18 : 24; // Smaller icons on mobile
      // Fill changes on hover for non-Rewards desktop buttons
      const fillColor = (isMobile && isActive) || (isMobile && isHovered) || (!isMobile && !isRewards && isHovered) ? '#014A5C' : 'none';
      
      switch (icon) {
        case 'collect':
          return (
            <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 30 30" fill={fillColor}>
              <path d="M27.9515 7.43694C27.9515 5.98682 27.3754 4.59608 26.35 3.57069C25.3246 2.5453 23.9339 1.96924 22.4838 1.96924H7.81432C6.3642 1.96924 4.97346 2.5453 3.94807 3.57069C2.92268 4.59608 2.34662 5.98682 2.34662 7.43694M27.9515 7.43694H2.34662M27.9515 7.43694L24.9509 14.2382M2.34662 7.43694L5.34719 14.2382M2.34662 14.2382H27.9515V28.1462H2.34662V14.2382ZM12.958 14.2382H17.3415C17.3468 14.2382 17.3518 14.2403 17.3556 14.2441C17.3594 14.2478 17.3615 14.2529 17.3615 14.2582V18.5057C17.3616 18.7963 17.3045 19.0841 17.1934 19.3526C17.0823 19.6211 16.9193 19.865 16.7138 20.0705C16.5084 20.276 16.2644 20.4389 15.9959 20.5501C15.7274 20.6612 15.4396 20.7183 15.149 20.7181C14.5626 20.7181 14.0002 20.4852 13.5856 20.0705C13.1709 19.6559 12.938 19.0935 12.938 18.507V14.2582C12.938 14.2529 12.9401 14.2478 12.9438 14.2441C12.9476 14.2403 12.9527 14.2382 12.958 14.2382Z" stroke={iconColor} strokeWidth="3.30385" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          );
        case 'refer':
          return (
            <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 31 30" fill={fillColor}>
              <path d="M16.8027 8.01828C16.3318 7.88925 15.8454 7.82602 15.3572 7.83038C14.3421 7.82934 13.3445 8.0956 12.4649 8.60239C11.5853 9.10918 10.8546 9.83863 10.3464 10.7174C9.83813 11.5962 9.57021 12.5932 9.56956 13.6084C9.56891 14.6236 9.83555 15.621 10.3427 16.5004C10.8498 17.3798 11.5795 18.1102 12.4585 18.6181C13.3375 19.126 14.3347 19.3936 15.3498 19.3939C16.365 19.3941 17.3623 19.1271 18.2415 18.6196C19.1207 18.1122 19.8508 17.3821 20.3584 16.503M22.5844 10.7214H28.3662M25.4753 7.83057V13.6123M28.3662 19.394V26.6212C28.3662 27.0046 28.2139 27.3722 27.9428 27.6433C27.6717 27.9144 27.3041 28.0667 26.9207 28.0667H3.79376C3.41041 28.0667 3.04276 27.9144 2.77169 27.6433C2.50061 27.3722 2.34833 27.0046 2.34833 26.6212V3.49426C2.34833 3.11091 2.50061 2.74326 2.77169 2.47219C3.04276 2.20111 3.41041 2.04883 3.79376 2.04883H25.4753M15.3572 19.394C13.4405 19.394 11.6022 20.1555 10.2469 21.5108C8.8915 22.8662 8.13007 24.7045 8.13007 26.6212V28.0667H22.5844V26.6212C22.5844 24.7045 21.823 22.8662 20.4676 21.5108C19.1123 20.1555 17.274 19.394 15.3572 19.394Z" stroke={iconColor} strokeWidth="3.30385" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          );
        case 'junk':
          return (
            <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 26 32" fill={fillColor}>
              <path d="M2.18884 5.30615H23.6926" stroke={iconColor} strokeWidth="3.30385" strokeLinecap="round"/>
              <path d="M6.79681 5.30585H6.96748C8.38132 5.30585 9.52745 4.15972 9.52745 2.74588C9.52745 2.46312 9.75668 2.23389 10.0394 2.23389H15.8421C16.1248 2.23389 16.354 2.46312 16.354 2.74588C16.354 4.15972 17.5001 5.30585 18.914 5.30585H19.0847" stroke={iconColor} strokeWidth="3.30385"/>
              <path d="M22.1567 11.4497L20.9727 25.6562C20.7738 28.0444 18.7773 29.8815 16.3808 29.8815H9.50079C7.10424 29.8815 5.10777 28.0444 4.90876 25.6562L3.72488 11.4497" stroke={iconColor} strokeWidth="3.30385" strokeLinecap="round"/>
            </svg>
          );
        case 'lottery':
          // Check if this is the rewards button on desktop
          if (!isMobile && isRewards) {
            // Desktop rewards: Use clover with layered circles
            return (
              <div style={{ position: 'relative', width: '40px', height: '40px' }}>
                {/* Black outer circle */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    backgroundColor: '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none', // No border on icon, border is on button container
                  }}
                >
                  {/* Gold middle circle */}
                  <div
                    style={{
                      width: '34px', // Increased to fill more space since no border
                      height: '34px',
                      borderRadius: '50%',
                      background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {/* Black inner circle */}
                    <div
                      style={{
                        width: '26px', // Proportionally increased
                        height: '26px',
                        borderRadius: '50%',
                        backgroundColor: '#000000', // Always black, never blue
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {/* Clover icon */}
                      <img 
                        src="/clover.svg" 
                        alt="Rewards" 
                        width="16" 
                        height="16"
                        style={{
                          filter: 'none', // Always normal, never black
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          // Mobile or Points button: Use star icon as before
          return (
            <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 32 30" fill={fillColor}>
              <path
                d="M16.0002 3.27295L19.2219 9.76091L26.378 10.7976L21.1891 15.8376L22.4436 22.9698L16.0002 19.5927L9.55673 22.9698L10.8113 15.8376L5.6224 10.7976L12.7785 9.76091L16.0002 3.27295Z"
                stroke={iconColor}
                strokeWidth="3.30385"
                strokeLinejoin="round"
              />
              <path
                d="M6.0459 26.7271H25.9546"
                stroke={iconColor}
                strokeWidth="3.30385"
                strokeLinecap="round"
              />
            </svg>
          );
      }
    };

    return (
      <Link
        to={to}
        className="flex-1 flex md:flex-row flex-col items-center justify-center transition-all relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          gap: window.innerWidth < 768 ? '4px' : '10px',
          background: !isMobile && isRewards
            ? (isHovered ? '#1B1B1B' : 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)')
            : (!isMobile && !isRewards && isHovered)
              ? '#FFFFFF'
              : isActive && !isRewards ? '#50E4F9' : 'transparent',
          textDecoration: 'none',
          cursor: 'pointer',
          // Mobile: use borderRight for separators
          // Desktop: different logic - rewards button gets full box border, others get borderRight for separators
          ...(isMobile 
            ? {
                borderRight: shouldShowRightBorder ? '2px solid #50E4F9' : 'none',
              }
            : isRewards
              ? {
                  border: '2px solid #FB8B00', // Orange border for desktop rewards (full box)
                  borderRadius: '4px', // Add slight rounding to make it a distinct button
                }
              : {
                  borderRight: shouldShowRightBorder ? '2px solid #50E4F9' : 'none',
                }
          ),
          overflow: 'visible', // Allow borders to extend
        }}
      >
        <div
          className="rounded-full border-2 flex items-center justify-center transition-all"
          style={{
            width: window.innerWidth < 768 ? '28px' : '40px',
            height: window.innerWidth < 768 ? '28px' : '40px',
            backgroundColor: isMobile && isHovered 
              ? '#FFFFFF' 
              : (!isMobile && !isRewards && isHovered)
                ? '#014A5C' // Blue background on hover for non-Rewards desktop
                : isActive && !isRewards ? '#262626' : 'transparent',
            borderColor: !isMobile && isRewards ? 'transparent' : '#50E4F9', // No border for rewards icon container
          }}
        >
          {getIcon()}
        </div>
        <span
          className="uppercase text-center"
          style={{
            fontFamily: 'Hyperspace Race Capsule, cursive',
            fontSize: window.innerWidth < 768 ? '14px' : '16px',
            fontWeight: window.innerWidth < 768 ? '400' : '700',
            color: !isMobile && isRewards && isHovered
              ? undefined // Use gradient for hovered rewards
              : !isMobile && isRewards 
                ? '#000000' // Black text on gold gradient for rewards (not hovered)
                : (!isMobile && !isRewards && isHovered)
                  ? '#014A5C' // Teal for hovered non-rewards desktop
                  : isActive ? '#000000' : '#FFFFFF', // Black when active, white when not
            background: !isMobile && isRewards && isHovered
              ? 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)'
              : undefined,
            WebkitBackgroundClip: !isMobile && isRewards && isHovered ? 'text' : undefined,
            WebkitTextFillColor: !isMobile && isRewards && isHovered ? 'transparent' : undefined,
            backgroundClip: !isMobile && isRewards && isHovered ? 'text' : undefined,
          }}
        >
          {text}
        </span>
      </Link>
    );
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40">
      {/* Elevated Rewards Button - Mobile Only */}
      {isMobile && (
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-50 w-[130px] h-[70px]">
          <Link to={AppRoutesEnum.Rewards} className="flex flex-col items-center justify-between h-full">
            {/* Layered circular button positioned at top of navbar */}
            <div
              className="rounded-full flex items-center justify-center transition-all hover:scale-105 -mt-[38px]"
              style={{
                width: '100px',
                height: '100px',
                border: '3px solid #50E4F9', // Blue border (outermost)
                padding: '0',
                boxShadow: '0 4px 20px rgba(80, 228, 249, 0.5)',
              }}
            >
              {/* Gold gradient border layer that turns black when active */}
              <div
                className="rounded-full flex items-center justify-center w-full h-full"
                style={{
                  background: location.pathname === AppRoutesEnum.Rewards 
                    ? '#000000' 
                    : 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                  padding: '4px',
                }}
              >
                {/* Center circle that turns gold when active */}
                <div
                  className="rounded-full flex items-center justify-center w-full h-full"
                  style={{
                    background: location.pathname === AppRoutesEnum.Rewards 
                      ? 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)' 
                      : '#000000',
                  }}
                >
                  {/* Clover placeholder image */}
                  <img 
                    src="/clover.svg" 
                    alt="Rewards" 
                    width="44" 
                    height="44"
                    style={{
                      filter: location.pathname === AppRoutesEnum.Rewards ? 'brightness(0)' : 'none',
                    }}
                  />
                </div>
              </div>
            </div>
            {/* Text at bottom of navbar with gradient */}
            <span
              className="text-xs uppercase pb-2"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                background: 'linear-gradient(93deg, #FB8B00 -40.78%, #F6DF6F 11.1%, #FB8B00 59.21%, #F6DF6F 98.82%, #FB8B00 155.42%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: 'none',
                fontSize: '14px',
              }}
            >
              REWARDS
            </span>
          </Link>
        </div>
      )}

      {/* Main Navbar */}
      <div
        className="flex w-full"
        style={{
          height: window.innerWidth < 768 ? '70px' : '55px',
          backgroundColor: window.innerWidth < 768 ? '#000000' : '#262626',
          borderTop: '2px solid #50E4F9',
          borderLeft: '2px solid #50E4F9',
          borderRight: '2px solid #50E4F9',
          borderRadius: '16px 16px 0 0',
          boxShadow:
            '0 -4px 20px rgba(80, 228, 249, 0.3), 0 -2px 10px rgba(80, 228, 249, 0.2)',
        }}
      >
        <NavButton to={AppRoutesEnum.Home} icon="junk" text="Junk" />
        <NavButton to={AppRoutesEnum.Chest} icon="collect" text="Chest" />
        {/* Spacer for mobile elevated button, or Rewards button for desktop */}
        {isMobile ? (
          <div 
            className="flex items-center justify-center"
            style={{ 
              width: '130px', 
              height: '100%',
              flexShrink: 0,
              borderLeft: '2px solid #50E4F9',
              borderRight: '2px solid #50E4F9',
              position: 'relative',
              boxSizing: 'border-box',
            }} 
          >
            {/* Visual separator lines to ensure visibility */}
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: '#50E4F9',
              zIndex: 20,
            }} />
            <div style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: '#50E4F9',
              zIndex: 20,
            }} />
          </div>
        ) : (
          <NavButton to={AppRoutesEnum.Rewards} icon="lottery" text="Rewards" />
        )}
        <NavButton to={AppRoutesEnum.Points} icon="lottery" text="Points" />
        <NavButton to={AppRoutesEnum.Referral} icon="refer" text="Refer" />
      </div>
    </div>
  );
}
